import {sendAnalyticsEvent} from '#/analytics/analytics';
import {ContentTile} from '#/atoms/content-tile';
import {TransactionTileRegular} from '#/components/transaction/transaction-tile';
import {LinkToUserPortfolio, UserAvatarName} from '#/components/user/link-avatar-name';
import {useUserProfile} from '#/hooks/query/use-user-profile';
import {useFormattedDistanceDate, useTransactionDate} from '#/hooks/use-transaction-date';
import {ClassName} from '#/utils/utils';
import {DisplayableShareLandTransaction, Land} from '@shareland/shareland-common';
import {FunctionComponent} from 'react';

type ProfileLinkTransactionTileProps = {
  land: Land;
  transaction: DisplayableShareLandTransaction;
} & ClassName;

export const ProfileLinkTransactionTile: FunctionComponent<ProfileLinkTransactionTileProps> = ({
  land,
  transaction,
  className,
}) => {
  // External hooks
  const {data: userProfileQuery} = useUserProfile({account: transaction.account, enabled: !transaction.userProfile});
  const transactionDate = useTransactionDate(transaction);

  // Vars
  const userProfile = transaction.userProfile || userProfileQuery || undefined;

  // Functions
  const onClick = () => {
    sendAnalyticsEvent.userPortfolioConsultation({
      account: transaction.account,
      hasProfilePicture: !!userProfile?.avatarUrl,
      hasUserName: !!userProfile?.displayName,
    });
  };

  return (
    <ContentTile className={`flex flex-col gap-4 ${className}`}>
      <LinkToUserPortfolio account={transaction.account} onClick={onClick} className='flex gap-2 items-center'>
        <UserAvatarName account={transaction.account} userProfile={userProfile} />
        <DateText date={transactionDate} />
      </LinkToUserPortfolio>
      <TransactionTileRegular land={land} transaction={transaction} />
    </ContentTile>
  );
};

export const DateText: FunctionComponent<{
  date: Date | null;
}> = ({date: inputDate}) => {
  const {date, distance} = useFormattedDistanceDate(inputDate);

  if (!(date || distance)) {
    return null;
  }

  if (distance) {
    return <span className='text-gray-400 whitespace-nowrap text-xs'>{distance}</span>;
  }

  if (date) {
    return (
      <span className='text-gray-400 whitespace-nowrap text-xs'>
        {date.split(', ').map((text, index) => (
          <span key={index} className='block text-right'>
            {text}
          </span>
        ))}
      </span>
    );
  }

  return null;
};
